import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Surfaces the browser's "Add to Home Screen" prompt as a dismissible
 * banner. We intercept the `beforeinstallprompt` event so we can choose
 * when to trigger it — stashing it in state and wiring the "Install"
 * button to call `prompt()`.
 *
 * Dismissal is persisted in localStorage for 30 days so we don't nag
 * users who've consciously said "not now". If the user actually
 * installs (`appinstalled` event fires), the banner goes away
 * immediately.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "lenavio.installPromptDismissedAt";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function wasRecentlyDismissed(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export function InstallPromptBanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null
  );
  const [dismissed, setDismissed] = useState<boolean>(() =>
    wasRecentlyDismissed()
  );

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!deferred || dismissed) return null;

  const handleInstall = async () => {
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } finally {
      // Either way, clear the deferred event — the browser only fires
      // `beforeinstallprompt` once per session anyway.
      setDeferred(null);
    }
  };

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* private mode — best effort */
    }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label="Install Lenavio"
      className="mb-4 flex items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3 shadow-sm"
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
        <Download className="h-4 w-4" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">
          Install Lenavio
        </p>
        <p className="text-xs text-text-muted">
          Add Lenavio to your home screen for one-tap access and offline
          support.
        </p>
      </div>
      <Button size="sm" onClick={handleInstall}>
        Install
      </Button>
      <button
        type="button"
        aria-label="Dismiss install prompt"
        onClick={handleDismiss}
        className="p-1.5 rounded text-text-muted hover:text-text-primary hover:bg-background transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
